import { useEffect, useState } from 'react';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import CoreBadge from '../../components/common/CoreBadge';
import { formatDateOnly } from '../../utils/date';
import { requisitosDaSubsecao } from '../../utils/catalogo';
import * as catalogoApi from '../../api/catalogo';
import * as hospitaisApi from '../../api/hospitais';
import * as avaliacoesApi from '../../api/avaliacoes';
import * as conformidadeApi from '../../api/conformidade';

export default function RelatorioImpressao({ idHospital, hospital }) {
  const [secoes, setSecoes] = useState(null);
  const [escopo, setEscopo] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState(null);
  const [geral, setGeral] = useState(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const [resCatalogo, resEscopo, resAvaliacoes, resGeral] = await Promise.all([
          catalogoApi.listarHierarquia(),
          hospitaisApi.obterEscopo(idHospital),
          avaliacoesApi.listarAtuais(idHospital),
          conformidadeApi.obter(idHospital, { formula: 'binaria' }),
        ]);
        if (cancelado) return;
        setSecoes(resCatalogo.data);
        setEscopo(resEscopo.data);
        setAvaliacoes(resAvaliacoes.data);
        setGeral(resGeral.data.percentual);
      } catch {
        // relatório de impressão — se falhar, a exportação sai incompleta silenciosamente,
        // sem travar a tela interativa (que já tem seu próprio tratamento de erro)
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [idHospital]);

  if (!secoes || !escopo || !avaliacoes || geral === null) {
    return <div className="relatorio-impressao" />;
  }

  const ativosPorId = new Map(escopo.filter((e) => e.ativo).map((e) => [e.id_requisito, true]));
  const avaliacaoPorRequisito = new Map(avaliacoes.map((a) => [a.id_requisito, a]));

  const secoesComRequisitos = secoes
    .map((secao) => {
      const linhas = secao.subsecoes.flatMap((sub) =>
        requisitosDaSubsecao(sub)
          .filter((r) => ativosPorId.has(r.id_requisito))
          .map((r) => ({ requisito: r, subsecao: sub }))
      );
      return { secao, linhas };
    })
    .filter((s) => s.linhas.length > 0);

  const vencimentos = avaliacoes
    .filter((a) => a.status_vigencia === 'a_vencer' || a.status_vigencia === 'vencido')
    .sort((a, b) => (a.data_vencimento_vigencia < b.data_vencimento_vigencia ? -1 : 1));

  return (
    <div className="relatorio-impressao">
      <div className="relatorio-impressao__cabecalho">
        <div>
          <h1>{hospital.nome}</h1>
          <p>{hospital.cnpj || 'CNPJ não informado'}</p>
        </div>
        <div className="relatorio-impressao__resumo">
          <div className="relatorio-impressao__percentual">{geral}%</div>
          <div>Conformidade geral (fórmula binária)</div>
          <div style={{ fontSize: 11 }}>Relatório gerado em {new Date().toLocaleDateString('pt-BR')}</div>
        </div>
      </div>

      {vencimentos.length > 0 && (
        <div className="relatorio-impressao__secao">
          <h2>Vigências a vencer ou vencidas ({vencimentos.length})</h2>
          <table className="am-table">
            <thead>
              <tr>
                <th>Requisito</th>
                <th>Status atual</th>
                <th>Vence em</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {vencimentos.map((v) => (
                <tr key={v.id_requisito}>
                  <td>
                    <strong>{v.codigo_requisito}</strong> — {v.texto_requisito}
                  </td>
                  <td>
                    <StatusBadge status={v.status} />
                  </td>
                  <td>{formatDateOnly(v.data_vencimento_vigencia)}</td>
                  <td>
                    <StatusBadge status={v.status_vigencia} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {secoesComRequisitos.map(({ secao, linhas }) => (
        <div key={secao.id_secao} className="relatorio-impressao__secao">
          <h2>
            Seção {secao.numero} — {secao.nome}
          </h2>
          <table className="am-table">
            <thead>
              <tr>
                <th>Subseção</th>
                <th>Código</th>
                <th>Requisito</th>
                <th>Status</th>
                <th>Fragilidade / Ação</th>
                <th>Vigência</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(({ requisito: r, subsecao: sub }) => {
                const avaliacao = avaliacaoPorRequisito.get(r.id_requisito);
                return (
                  <tr key={r.id_requisito}>
                    <td style={{ whiteSpace: 'nowrap' }}>{sub.numero}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.codigo}
                      {r.core && <CoreBadge />}
                    </td>
                    <td>{r.texto}</td>
                    <td>{avaliacao ? <StatusBadge status={avaliacao.status} /> : <Pill tone="outline">Não avaliado</Pill>}</td>
                    <td>
                      {avaliacao?.fragilidade && <div>{avaliacao.fragilidade}</div>}
                      {avaliacao?.acao_proposta && <div>{avaliacao.acao_proposta}</div>}
                      {avaliacao?.prazo_acao && <div>Prazo: {formatDateOnly(avaliacao.prazo_acao)}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {avaliacao ? (
                        <>
                          <StatusBadge status={avaliacao.status_vigencia} />
                          <div style={{ fontSize: 10 }}>{formatDateOnly(avaliacao.data_vencimento_vigencia)}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
